<template>
    <Toast />
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <div>
                        <h5 class="m-0">Historial de Conteos de Inventario</h5>
                        <small class="text-color-secondary">Revise cada conteo y aplique sus ajustes al stock.</small>
                    </div>
                    <div class="flex gap-2 mt-2 md:mt-0">
                        <Button label="Ajustes de inventario" icon="pi pi-chart-line" class="p-button-text" @click="$router.push('/dashboard/inventario/ajustes')" />
                        <Button label="Nuevo conteo" icon="pi pi-plus" class="p-button-success" @click="$router.push('/dashboard/inventario/conteo')" />
                    </div>
                </div>

                <div class="flex flex-column md:flex-row md:justify-content-end md:align-items-end gap-2 mb-3">
                    <div>
                        <label class="block text-sm">Estado</label>
                        <Dropdown v-model="filters.status" :options="statusOptions" optionLabel="label" optionValue="value" placeholder="Todos" showClear />
                    </div>
                    <div>
                        <label class="block text-sm">Desde</label>
                        <Calendar v-model="filters.initialDate" dateFormat="yy-mm-dd" showIcon />
                    </div>
                    <div>
                        <label class="block text-sm">Hasta</label>
                        <Calendar v-model="filters.endDate" dateFormat="yy-mm-dd" showIcon />
                    </div>
                    <div>
                        <Button icon="pi pi-search" class="p-button-rounded apply-filter-btn" v-tooltip.top="'Aplicar filtros'" @click="loadList(1)" />
                    </div>
                </div>

                <DataTable
                    :value="items"
                    :loading="loading"
                    :paginator="true"
                    :rows="pageSize"
                    :totalRecords="totalItems"
                    :lazy="true"
                    :first="(currentPage - 1) * pageSize"
                    @page="onPage"
                    responsiveLayout="scroll"
                    class="p-datatable-sm clickable-rows"
                    @row-click="openDetail($event.data.id)"
                >
                    <template #empty>No hay conteos registrados.</template>
                    <Column field="id" header="N" style="width:5rem" />
                    <Column header="Fecha">
                        <template #body="{ data }">{{ formatDate(data.createdAt) }}</template>
                    </Column>
                    <Column header="Contado por">
                        <template #body="{ data }">
                            {{ data.creator }} <Tag :value="data.creatorRol" severity="info" class="ml-2" />
                        </template>
                    </Column>
                    <Column header="Alcance">
                        <template #body="{ data }">{{ data.categoryName || 'Todos' }}</template>
                    </Column>
                    <Column header="Contados">
                        <template #body="{ data }">{{ data.linesCounted }} / {{ data.linesTotal }}</template>
                    </Column>
                    <Column header="Faltantes / Sobrantes">
                        <template #body="{ data }">
                            <span class="text-red-500 font-medium">{{ data.shortageCount }}</span> /
                            <span class="text-blue-500 font-medium">{{ data.overageCount }}</span>
                        </template>
                    </Column>
                    <Column header="Valor estimado">
                        <template #body="{ data }"><span :class="diffClass(data.totalValueDifference)">{{ formatMoney(data.totalValueDifference) }}</span></template>
                    </Column>
                    <Column header="Resultado">
                        <template #body="{ data }"><Tag :value="resultLabel(data.result)" :severity="resultSeverity(data.result)" /></template>
                    </Column>
                    <Column header="Estado">
                        <template #body="{ data }"><Tag :value="statusLabel(data.status)" :severity="statusSeverity(data.status)" /></template>
                    </Column>
                </DataTable>
            </div>
        </div>
    </div>
</template>

<script>
import httpRequest from '../../../service/httpRequest';
import { localISODate } from '../../../service/dates';
import { diffClass, formatDate, formatMoney, resultLabel, resultSeverity, statusLabel, statusSeverity } from './inventoryFormat';

export default {
    name: 'InventoryCountList',
    data() {
        return {
            loading: false,
            items: [],
            currentPage: 1,
            pageSize: 25,
            totalItems: 0,
            filters: { status: null, initialDate: null, endDate: null },
            statusOptions: [
                { value: 'OPEN', label: 'Vigente' },
                { value: 'APPLIED', label: 'Aplicado' },
                { value: 'VOIDED', label: 'Anulado' },
            ],
        };
    },
    created() {
        if (!this.$can('inventoryManage')) {
            this.$router.replace('/dashboard/inventario/conteo');
            return;
        }
        this.loadList(1);
    },
    methods: {
        diffClass, formatDate, formatMoney, resultLabel, resultSeverity, statusLabel, statusSeverity,
        async loadList(page) {
            this.loading = true;
            this.currentPage = page;
            try {
                const r = await httpRequest.getInventoryCounts({
                    page,
                    pageSize: this.pageSize,
                    status: this.filters.status,
                    initialDate: this.toIso(this.filters.initialDate),
                    endDate: this.toIso(this.filters.endDate),
                });
                if (r.status === 200 && r.data) {
                    this.items = r.data.items || [];
                    this.totalItems = r.data.totalItems || 0;
                } else {
                    this.$toast.add({ severity: 'error', summary: 'Error', detail: r.error || 'No se pudo cargar', life: 4000 });
                }
            } catch (e) {
                console.error(e);
                this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Fallo de red', life: 4000 });
            } finally {
                this.loading = false;
            }
        },
        onPage(event) {
            this.loadList(event.page + 1);
        },
        openDetail(id) {
            this.$router.push({ path: '/dashboard/inventario/conteo/detalle', query: { id } });
        },
        toIso(d) {
            if (!d) return null;
            // Fecha de calendario local, no UTC: de noche toISOString() ya es «mañana» (H64).
            return localISODate(d);
        },
    },
};
</script>

<style scoped>
.clickable-rows :deep(tbody tr) {
    cursor: pointer;
}
.apply-filter-btn {
    background: var(--primary-color) !important;
    border-color: var(--primary-color) !important;
    color: var(--primary-color-text, #fff) !important;
}
</style>
